import type { TextContentInfo, HeadingInfo, AlertInfo, ScrollInfo, ContentSize } from '../types/observations';
import { generateSelector } from '../utils/selector-utils';
import { isElementVisible, getElementPosition, isElementInViewport } from '../utils/dom-utils';

export class TextContentAnalyzer {
  /**
   * Get all text content from the page (visible text paragraphs, spans, divs)
   * This captures static text that isn't interactive
   */
  getTextContent(maxItems: number = 100): TextContentInfo[] {
    const textElements: TextContentInfo[] = [];

    // Selectors for elements that typically contain readable text
    const textSelectors = [
      'p',
      'span',
      'div',
      'li',
      'td',
      'th',
      'label',
      'footer',
      'header',
      'section',
      'article',
      '[role="alert"]',
      '.error',
      '.error-message',
      '.success',
      '.warning',
      '.info'
    ];

    const seen = new Set<string>();

    for (const selector of textSelectors) {
      const elements = document.querySelectorAll(selector);

      for (const element of Array.from(elements)) {
        if (textElements.length >= maxItems) break;

        // Get direct text content (not inherited from children)
        const text = this.getDirectTextContent(element);

        if (!text || text.length < 3) continue; // Skip empty or very short text

        // Skip if we've already seen this text
        const key = `${element.tagName}:${text}`;
        if (seen.has(key)) continue;
        seen.add(key);

        const isVisible = isElementVisible(element);
        if (!isVisible) continue; // Only include visible text

        try {
          const elementSelector = generateSelector(element);
          const position = getElementPosition(element);
          const inViewport = isElementInViewport(element);

          textElements.push({
            selector: elementSelector,
            text,
            tagName: element.tagName.toLowerCase(),
            isVisible,
            position,
            inViewport
          });
        } catch (error) {
          // Skip elements that fail selector generation
          continue;
        }
      }
    }

    return textElements;
  }

  /**
   * Get all headings from the page
   */
  getHeadings(): HeadingInfo[] {
    const headings: HeadingInfo[] = [];

    for (let level = 1; level <= 6; level++) {
      const elements = document.querySelectorAll(`h${level}`);

      for (const element of Array.from(elements)) {
        const text = element.textContent?.trim();
        if (!text) continue;

        if (!isElementVisible(element)) continue;

        try {
          const selector = generateSelector(element);
          const inViewport = isElementInViewport(element);

          headings.push({
            level,
            text,
            selector,
            inViewport
          });
        } catch (error) {
          continue;
        }
      }
    }

    return headings;
  }

  /**
   * Get alerts and error messages
   */
  getAlerts(): AlertInfo[] {
    const alerts: AlertInfo[] = [];

    // Common alert patterns
    const alertSelectors = [
      { selector: '[role="alert"]', type: 'error' as const },
      { selector: '.alert-danger', type: 'error' as const },
      { selector: '.alert-error', type: 'error' as const },
      { selector: '.error', type: 'error' as const },
      { selector: '.error-message', type: 'error' as const },
      { selector: '[class*="error"]', type: 'error' as const },
      { selector: '.alert-warning', type: 'warning' as const },
      { selector: '.warning', type: 'warning' as const },
      { selector: '.alert-info', type: 'info' as const },
      { selector: '.info', type: 'info' as const },
      { selector: '.alert-success', type: 'success' as const },
      { selector: '.success', type: 'success' as const },
    ];

    const seen = new Set<string>();

    for (const { selector: pattern, type } of alertSelectors) {
      const elements = document.querySelectorAll(pattern);

      for (const element of Array.from(elements)) {
        const message = element.textContent?.trim();
        if (!message || message.length < 3) continue;

        if (seen.has(message)) continue;
        seen.add(message);

        if (!isElementVisible(element)) continue;

        try {
          const selector = generateSelector(element);
          const role = element.getAttribute('role') || undefined;

          alerts.push({
            type,
            message,
            selector,
            role
          });
        } catch (error) {
          continue;
        }
      }
    }

    return alerts;
  }

  /**
   * Get scroll information
   */
  getScrollInfo(): ScrollInfo {
    const scrollX = window.scrollX || window.pageXOffset;
    const scrollY = window.scrollY || window.pageYOffset;

    const maxScrollX = Math.max(0, document.documentElement.scrollWidth - window.innerWidth);
    const maxScrollY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);

    return {
      x: scrollX,
      y: scrollY,
      maxX: maxScrollX,
      maxY: maxScrollY,
      atTop: scrollY <= 0,
      atBottom: scrollY >= maxScrollY - 10, // 10px tolerance
      atLeft: scrollX <= 0,
      atRight: scrollX >= maxScrollX - 10
    };
  }

  /**
   * Get content size (full page dimensions)
   */
  getContentSize(): ContentSize {
    return {
      width: document.documentElement.scrollWidth,
      height: document.documentElement.scrollHeight
    };
  }

  /**
   * Get direct text content of an element (not including nested elements)
   */
  private getDirectTextContent(element: Element): string {
    let text = '';

    for (const node of Array.from(element.childNodes)) {
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent || '';
      }
    }

    return text.trim();
  }
}
